/* eslint-disable no-console */
/** @typedef {import("@insightfulscience/shared-utils/http-client/types").IHttpClient} IHttpClient */
/** @typedef {import("../interfaces/ISGApiClient").ISGApiClient} ISGApiClient */

import { throwError } from "../utils/throw-error";
import { preparePlasmid, prepareSetData, prepareSequences } from "../domain/prepare-plasmids";

/** @type {(conn: IHttpClient) => ISGApiClient} */
const sgApiClient = ({ get }) => ({
	getSets: () =>
		get("plasmids/sets").then(({ status, statusText, body }) =>
			// prettier-ignore
			status === 200 ? body.sets.map(prepareSetData) :
			status === 404 ? [] : 
			throwError(`Failed to fetch sets: ${status} - ${statusText}`),
		),

	getSequences: setId =>
		get(`plasmids/sequences/${setId}`).then(({ status, statusText, body }) =>
			// prettier-ignore
			status === 200 ? prepareSequences(body) :
			status === 404 ? null :
			throwError(`Failed to fetch sequences of set #${setId}: ${status} - ${statusText}`),
		),

	getPlasmid: (setId, plasmidId) =>
		get(`plasmids/sequences/${setId}/plasmid/${plasmidId}`).then(({ status, statusText, body }) =>
			// prettier-ignore
			status === 200 ? preparePlasmid(body.plasmid) :
			status === 404 ? null :
			throwError(`Failed to fetch plasmid #${setId}/${plasmidId}: ${status} - ${statusText}`),
		),

	getPlasmidContentMap: (setId, plasmidId) =>
		get(`plasmids/map-content/${setId}/plasmid/${plasmidId}?token=map1&fast=0`).then(
			({ status, statusText, body }) =>
				// prettier-ignore
				status === 200 ? body :
				status === 404 ? null :
				throwError(
					`Failed to fetch plasmid content map #${setId}/${plasmidId}: ${status} - ${statusText}`,
				),
		),

	getPlasmidStaticImage: (setId, plasmidId) =>
		get(`plasmids/sequences/${setId}/plasmid/${plasmidId}/static-image`).then(
			({ status, statusText, body }) =>
				// prettier-ignore
				status === 200 ? body.url :
				status === 404 ? null :
				throwError(
					`Failed to fetch plasmid static image #${setId}/${plasmidId}: ${status} - ${statusText}`,
				),
		),
	searchPlasmid: string =>
		get("plasmids/search", {
			query: {
				string,
			},
		}).then(({ status, statusText, body }) =>
			// prettier-ignore
			status === 200 ? body.map(prepareSequences) :
			status === 404 ? [] :
			throwError(`Failed to search plasmids matching "${string}": ${status} - ${statusText}`),
		),

	getSequenceLineHeights: (
		setId,
		plasmidId,
		length,
		{ token = "seq1", enzymes = 1, features = 1, primers = 1, translations = 0 } = {},
	) =>
		get(`plasmids/sequence-content/${setId}/plasmid/${plasmidId}`, {
			query: {
				token,
				enzymes,
				features,
				primers,
				translations,
				length,
				asJson: 1,
			},
		}).then(({ status, statusText, body }) =>
			// prettier-ignore
			status === 200 ? body :
			status === 404 ? null :
			throwError(
				`Failed to fetch plasmid static image #${setId}/${plasmidId}: ${status} - ${statusText}`,
			),
		),

	getSequenceLine: (
		setId,
		plasmidId,
		length,
		base,
		{ enzymes = 1, features = 1, primers = 1, translations = 0 } = {},
	) =>
		get(`plasmids/sequence-line/${setId}/plasmid/${plasmidId}`, {
			query: {
				length,
				base,
				enzymes,
				features,
				primers,
				translations,
			},
		}).then(result => (result.ok ? result.body : "")),
});

export default sgApiClient;
